import styled from "@emotion/styled";
import { darkGrey, formatPercentage, green, red, transparentWhite, white } from "@stepfinance/react";
import { Col, Row, Statistic } from "antd";
import type { FC } from "react";

import { useLanguageContext } from "../../../contexts/language/language";
import { useCalculatePriceForDisplayCurrency } from "../../../hooks";
import type { MarginAccount } from "../../../types/margin";
import { MarginAccountSelect } from "./MarginAccountSelect";

type MarginOverviewProps = {
  marginAccounts: MarginAccount[];
  selectedAccount: MarginAccount | undefined;
  onAccountChange: (marginAccount: MarginAccount | undefined) => void;
};
export const MarginOverview: FC<MarginOverviewProps> = ({ marginAccounts, selectedAccount, onAccountChange }) => {
  const calculatePriceForDisplayCurrency = useCalculatePriceForDisplayCurrency();
  const selectedAccounts = selectedAccount ? [selectedAccount] : marginAccounts;
  const { collateralRatio, leverage, interest, equity } = selectedAccounts.reduce<OverviewData>(
    aggregateOverviewValues,
    {
      leverage: 0,
      collateralRatio: 0,
      equity: 0,
      interest: 0,
    },
  );

  const {
    currentLanguageData: {
      common,
      pages: {
        dashboard: {
          modules: {
            marginTrading: { marginOverview: marginOverviewText },
          },
        },
      },
    },
  } = useLanguageContext();

  const interestColor = interest === 0 ? transparentWhite : interest > 0 ? green : red;
  const collateralOver200 = collateralRatio > 200;
  return (
    <OverViewContainer>
      <div className="header">
        <span className="title">{marginOverviewText.title}</span>
        <MarginAccountSelect
          marginAccounts={marginAccounts}
          selectedAccount={selectedAccount}
          onChange={onAccountChange}
        />
      </div>
      <div className="overview-stats">
        <Row className="overview-row" gutter={10} style={{ rowGap: 10 }}>
          <Col className="overview-col" xs={{ span: 24 }} sm={{ span: 12 }} xxl={{ span: 6 }}>
            <StyledStatistic
              title={marginOverviewText.collateralRatioTitle}
              value={collateralOver200 ? ">200%" : Math.round(collateralRatio) + "%"}
              valueStyle={{ color: white }}
            />
          </Col>
          <Col xs={{ span: 24 }} sm={{ span: 12 }} xxl={{ span: 6 }}>
            <StyledStatistic valueStyle={{ color: white }} title={common.leverage} value={leverage.toFixed(2) + "X"} />
          </Col>
          <Col xs={{ span: 24 }} sm={{ span: 12 }} xxl={{ span: 6 }}>
            <StyledStatistic
              title={marginOverviewText.interestOfPositions}
              valueStyle={{ color: interestColor }}
              value={formatPercentage(interest, { performance: true })}
            />
          </Col>
          <Col xs={{ span: 24 }} sm={{ span: 12 }} xxl={{ span: 6 }}>
            <StyledStatistic
              valueStyle={{ color: white }}
              title={marginOverviewText.totalEquity}
              value={calculatePriceForDisplayCurrency(equity)}
            />
          </Col>
        </Row>
      </div>
    </OverViewContainer>
  );
};

const OverViewContainer = styled.div`
  margin-bottom: 30px;
  width: 100%;
  .title {
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0em;
    text-align: left;
  }
  .header {
    display: flex;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .overview-stats {
    display: flex;
    justify-content: space-between;
    .overview-row {
      width: calc(100% + 10px);
    }
  }
`;
const StyledStatistic = styled(Statistic)`
  background: ${darkGrey};
  height: 118px;
  padding: 30px;
  border-radius: 8px;
  .ant-statistic-title {
    font-weight: normal;
    font-size: 14px;
    color: ${transparentWhite};
    margin: 0px;
  }
  .ant-statistic-content {
    .ant-statistic-content-value {
      font-weight: 700;
      font-size: 24px;
      line-height: 32px;
      font-family: "Space Mono";
    }
  }
`;

interface OverviewData {
  collateralRatio: number;
  leverage: number;
  interest: number;
  equity: number;
}

const aggregateOverviewValues = (totals: OverviewData, account: MarginAccount): OverviewData => {
  const leverage = totals.leverage + account.leverage;
  const equity = totals.equity + account.equity;
  const weightedTotal = totals.equity / equity;
  const weightedCurrent = account.equity / equity;
  const collateralRatio = totals.collateralRatio * weightedTotal + weightedCurrent * account.collateralRatio;
  const interest = (totals.interest * totals.equity + account.equity * account.interest) / equity;
  return {
    leverage: isNaN(leverage) ? 0 : leverage,
    collateralRatio: isNaN(collateralRatio) ? 0 : collateralRatio,
    equity: isNaN(equity) ? 0 : equity,
    interest: isNaN(interest) ? 0 : interest,
  };
};
